import {
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type SortingState,
  type TableMeta,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useState } from 'react'
import { Link } from 'react-router'

import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

/* widen TanStack's per-column meta so column defs can pin a stable width
 * (e.g. `meta: { width: '180px' }`). when any column on a table sets it,
 * the table flips to `table-layout: fixed` so columns don't bounce when
 * row content changes (e.g. during a search filter). columns without an
 * explicit width split the remaining space.
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    width?: string
    /* alignment for the cell + header content. omit for left-aligned. */
    align?: 'left' | 'right' | 'center'
    /* opt this cell out of the row-level `<Link>` wrap that getRowHref
     * normally applies. set on columns that render their own interactive
     * elements (nested Link, button, DropdownMenu) so we don't end up
     * with invalid nested anchors or hijacked click handlers.
     */
    excludeFromRowLink?: boolean
  }

  interface TableMeta<TData extends RowData> {
    selection?: {
      isSelected: (row: TData) => boolean
      isPageSelected: boolean
      togglePage: () => void
    }
  }
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  /* preferred for navigation: wraps each cell in <Link> so the browser
   * shows the destination url on hover and cmd/ctrl-click opens a new tab.
   */
  getRowHref?: (row: TData) => string
  /* router state on each row link, read by the detail page's breadcrumb. */
  rowLinkState?: unknown
  /* fallback for non-navigation actions (mutations, modals, etc.) */
  onRowClick?: (row: TData) => void
  emptyMessage?: string
  /* when true, header renders normally and the body collapses to a single
   * spinner cell. preserves table chrome so layout doesn't jump when rows
   * arrive. */
  isLoading?: boolean
  /* server-side (controlled) sort. pass both to drive `ORDER BY` on the
   * backend: the column header toggles `sorting`, the page feeds it to its
   * list query, and the table renders rows in server order (manualSorting,
   * no client re-sort). omit both for the legacy client-side sort. */
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  meta?: TableMeta<TData>
}

export function DataTable<TData, TValue>({
  columns,
  data,
  getRowHref,
  rowLinkState,
  onRowClick,
  emptyMessage = 'No results.',
  isLoading = false,
  sorting: controlledSorting,
  onSortingChange,
  meta
}: DataTableProps<TData, TValue>) {
  const [localSorting, setLocalSorting] = useState<SortingState>([])
  /* controlled when the caller owns sort state (server-side); otherwise the
   * table sorts the rows it was handed, client-side. */
  const isServerSorted =
    controlledSorting !== undefined && Boolean(onSortingChange)

  const table = useReactTable({
    data,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: isServerSorted,
    onSortingChange: isServerSorted ? onSortingChange : setLocalSorting,
    state: { sorting: isServerSorted ? controlledSorting : localSorting },
    ...(isServerSorted ? {} : { getSortedRowModel: getSortedRowModel() })
  })

  const interactive = Boolean(getRowHref) || Boolean(onRowClick)
  const hasFixedWidths = columns.some(column => column.meta?.width)

  return (
    /* desktop-only by construction: every list pairs this with a <MobileList>,
     * so the breakpoint lives here rather than in each page's wrapper div. */
    <div className='hidden overflow-hidden rounded-md border sm:block'>
      <Table style={hasFixedWidths ? { tableLayout: 'fixed' } : undefined}>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const meta = header.column.columnDef.meta
                return (
                  <TableHead
                    key={header.id}
                    style={meta?.width ? { width: meta.width } : undefined}
                    className={cn(
                      'px-4',
                      alignClass(meta?.align),
                      /* under table-layout: fixed, clip at the column edge so an
                       * over-long cell never bleeds into its neighbour */
                      hasFixedWidths && 'overflow-hidden'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow className='hover:bg-transparent'>
              <TableCell colSpan={columns.length} className='h-48'>
                <div className='flex items-center justify-center'>
                  <Spinner />
                </div>
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map(row => {
              const href = getRowHref?.(row.original)
              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={
                    !href && onRowClick
                      ? () => onRowClick(row.original)
                      : undefined
                  }
                  className={
                    interactive
                      ? 'group cursor-pointer hover:bg-muted/50'
                      : undefined
                  }
                >
                  {row.getVisibleCells().map(cell => {
                    const meta = cell.column.columnDef.meta
                    const content = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )
                    if (href && !meta?.excludeFromRowLink) {
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            'p-0',
                            alignClass(meta?.align),
                            hasFixedWidths && 'overflow-hidden'
                          )}
                        >
                          <Link
                            to={href}
                            state={rowLinkState}
                            className='block px-4 py-2.5 text-inherit no-underline'
                          >
                            {content}
                          </Link>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'px-4',
                          alignClass(meta?.align),
                          hasFixedWidths && 'overflow-hidden'
                        )}
                      >
                        {content}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className='h-24 text-center text-sm text-muted-foreground'
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function alignClass(align: 'left' | 'right' | 'center' | undefined) {
  if (align === 'right') return 'text-right'
  if (align === 'center') return 'text-center'
  return undefined
}
